import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 1.5 visual preview at /preview/lesson/1.5
 *
 * "Where AI sits and what happens to your data" — Week 1 capstone.
 * Two halves:
 *   1. The map: AI > ML > Generative AI > LLMs > Chatbots/Agents, plus modality
 *   2. Your data: what happens when you hit Send, plus three working rules
 * Promotes "Modality" in as a new vocab term with the same 4-block treatment
 * used in 1.3 and 1.4.
 */

export default function LessonPreviewPage1_5() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

function LessonPage() {
  const totalSections = 6;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const ids = [
      'sec-cold-open',
      'sec-from-last-time',
      'sec-teaching',
      'sec-your-data',
      'sec-quick-check',
      'sec-lock-card',
    ];
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = ids.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[200px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <ColdOpen />
          <FromLastTime />
          <Teaching />
          <YourData />
          <QuickCheck />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

// ---------- Header ----------

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 1 · Foundations · Lesson 1.5
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        Where AI sits and what happens to your data
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 35 min
        </span>
        <span className="h-3 w-px bg-line" />
        <span>Threads: Judgment · Character</span>
        <span className="h-3 w-px bg-line" />
        <span className="text-amber">Week 1 capstone</span>
      </div>
    </header>
  );
}

// ---------- Cold open ----------

function ColdOpen() {
  return (
    <Section
      id="sec-cold-open"
      label="Cold open"
      eyebrow="Two questions before we start."
      accent="amber"
    >
      <div className="my-2 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          Scenario
        </p>
        <p className="mt-3 text-text">
          You open a fresh ChatGPT tab. You paste in your full college essay,
          your home address, and a private family document you were not
          supposed to share. You ask: <span className="italic">"Can you
          help me think through this?"</span>
        </p>
      </div>
      <p>
        Question one: where does that text go in the half-second between you
        hitting Send and the AI starting to reply?
      </p>
      <p>
        Question two: which of those three things you just pasted is still
        sitting on a server somewhere, six months from now, even after you
        delete the chat?
      </p>
      <p>
        If you cannot answer either question in one sentence, you do not
        actually know how to use AI safely. You know how to type. You do not
        know what happens to what you type.
      </p>
      <p className="text-text">
        Lesson 1.5 fixes that. First half is the map: where AI sits, what kind
        of AI you are actually talking to, and what it can see. Second half is
        your data: where it goes, who keeps it, and the three rules you run on
        every conversation.
      </p>
    </Section>
  );
}

// ---------- From last time ----------

function FromLastTime() {
  return (
    <Section
      id="sec-from-last-time"
      label="From last time"
      eyebrow="Three questions. No looking back at 1.4."
      accent="blue"
    >
      <ol className="space-y-3 text-text">
        <li>
          <span className="font-mono text-amber">01.</span> Name the four
          checks in the audit framework, in order.
        </li>
        <li>
          <span className="font-mono text-amber">02.</span> What is training
          cutoff and why is it check number one?
        </li>
        <li>
          <span className="font-mono text-amber">03.</span> If an audit finds
          a fabricated source, what are your three options?
        </li>
      </ol>
      <p className="mt-5 text-sm text-muted">
        Hold your answers. 1.5 hands you the missing context for everything
        you learned in 1.1 through 1.4: where this stuff sits and what it does
        with what you say to it.
      </p>
    </Section>
  );
}

// ---------- Teaching (Part 1: The map) ----------

function Teaching() {
  return (
    <Section
      id="sec-teaching"
      label="Part 1 · The map"
      eyebrow="What ChatGPT actually is, and what it is not."
    >
      <Subhead n="1" title="AI is not one thing" />
      <p>
        Most people use the word "AI" to mean ChatGPT. That is like using the
        word "vehicle" to mean your specific car. ChatGPT is one product, made
        by one company, that uses one type of AI. There is a whole landscape
        underneath that word, and knowing the layers makes everything else in
        this course make sense.
      </p>
      <p>
        Run through it from biggest to smallest. Each box sits inside the box
        above it.
      </p>

      <LandscapeMap />

      <p className="mt-2">
        When someone says <Vocab>AI did this</Vocab>, they almost always mean
        the smallest box: a chatbot or agent powered by an LLM. The bigger
        boxes still exist; they are doing other jobs, mostly in the
        background. Spam filters, fraud detection, the recommendation engine
        on YouTube. All AI. None of it is the thing you talk to.
      </p>

      <Subhead n="2" title="The new vocab: modality" />
      <p>
        Open ChatGPT, Claude, or Gemini in 2026 and the first thing you see
        is not a text box. It is a microphone icon, a camera icon, an upload
        button. These tools stopped being text-only sometime in 2024 and
        nobody made a big deal about it. The word for this is{' '}
        <Vocab>modality</Vocab>.
      </p>

      <VocabCard
        term="Modality"
        definition="A type of input or output a model can handle. Text, image, audio, and video are the four modalities you will run into."
        analogy="Think of a model like a person with senses. A text-only model can only read and write. A multimodal model can also look, listen, and speak."
        hook="Modality means mode of input. Text is one mode. A picture is another mode. Voice is another mode."
        matters="The same model can be wrong in different ways depending on which modality you use. A model that reads your essay well may completely misread your screenshot of that essay. Knowing what modality you are using changes how you audit the answer."
      />

      <p>
        One practical consequence: a screenshot of text is not the same as
        the text itself. When you paste an article, the model reads it
        directly. When you upload a screenshot, the model has to first see
        the image, recognize that it contains text, read the text, and then
        answer. Three more places for things to go wrong.
      </p>

      <Subhead n="3" title="The shorthand to remember" />
      <p>
        From here on, when this course says <Vocab>the AI</Vocab>, it means a
        specific thing: a chatbot or agent powered by an LLM, talking to you
        through one or more modalities. That is the box you live in for the
        rest of the program. The other boxes are real and important, but they
        are not who you are negotiating with at the keyboard.
      </p>
    </Section>
  );
}

// ---------- Teaching (Part 2: Your data) ----------

function YourData() {
  return (
    <Section
      id="sec-your-data"
      label="Part 2 · Your data"
      eyebrow="Where it goes. Who keeps it. What to do about it."
      accent="rose"
    >
      <Subhead n="4" title="What happens when you hit Send" />
      <p>
        You type a message. You hit Send. Stop the tape right there. Four
        things happen before the AI says anything back, and most people have
        no idea any of them are happening.
      </p>

      <DataFlow />

      <p className="mt-2">
        Every step in that chain is a place where your message exists,
        outside your control, on hardware you do not own. Some of those
        places forget it in seconds. Some of those places keep it for years.
        It depends entirely on the product and the settings.
      </p>

      <Subhead n="5" title="Three things that may happen to your text" />
      <p>
        Not three things that <span className="italic">will</span> happen.
        Three things that <Vocab>may</Vocab> happen, depending on the
        product, your account, and what you have agreed to without reading.
      </p>

      <div className="my-6 space-y-3">
        <DataFateCard
          n={1}
          title="Trained on"
          body="Your message could become part of the next version of the model. Your prompt and the AI's response feed back into training data. Some products do this by default. Some let you opt out. Some never do it."
        />
        <DataFateCard
          n={2}
          title="Retained"
          body="Even if it is not used for training, the conversation may sit on the company's servers for 30 days, 6 months, or indefinitely, depending on the policy. Deleting the chat in the app does not always delete the copy on the server."
        />
        <DataFateCard
          n={3}
          title="Reviewed"
          body="Real humans, employed by the AI company or its contractors, may read a sample of conversations to check for safety, quality, or abuse. You do not know which conversations. You do not get a notification."
        />
      </div>

      <p>
        None of this makes AI evil. Most of it is normal product behavior.
        The point is that you should know the rules of the game before you
        play it, not after.
      </p>

      <Subhead n="6" title="The three working rules" />
      <p>
        These are not bumper stickers. These are the three things to actually
        do, in order, before and during any AI conversation.
      </p>

      <div className="my-6 space-y-3">
        <RuleCard
          n={1}
          title="The stranger test"
          line="Before you paste anything, ask: would I be comfortable if a stranger on a bus read this over my shoulder? If the answer is no, do not paste it. Find another way."
        />
        <RuleCard
          n={2}
          title="Check the settings, once per tool"
          line="Every AI product has a privacy or data controls page. Find it. Turn off training on your data if the option exists. Set retention to the shortest window you can. Do it once per tool, then forget it."
        />
        <RuleCard
          n={3}
          title="Assume permanence"
          line="Treat every message like it is being recorded. Because some of them are. Once text leaves your machine, you cannot get it back. Plan accordingly."
        />
      </div>

      <p>
        These three rules are not paranoid. They are the same rules a careful
        adult applies to any system they do not control. Email. Cloud
        storage. Group chats. AI is just the newest version.
      </p>
    </Section>
  );
}

// ---------- Quick check ----------

function QuickCheck() {
  const items = [
    {
      q: 'Why is ChatGPT a bad shorthand for the word "AI"?',
      a: 'Because AI is the umbrella term covering everything from spam filters to self-driving cars. ChatGPT is one product, made by one company, using one kind of AI (an LLM). Saying "AI" when you mean "ChatGPT" hides a huge amount of context.',
    },
    {
      q: 'What does modality mean and why does it matter to auditing?',
      a: 'Modality is the type of input or output a model can handle: text, image, audio, video. It matters because the same model can be reliable in one modality and unreliable in another. A screenshot of text is a different audit than the same text typed in.',
    },
    {
      q: 'Name the four things that happen between you hitting Send and the AI replying.',
      a: 'Your text travels over the internet to the company\'s servers, gets pre-processed (tokenized, checked for policy violations), gets fed to the model, and the model generates a response one token at a time. Then the response travels back.',
    },
    {
      q: 'What are the three things that may happen to a message after you send it?',
      a: 'It may be used to train future versions of the model. It may be retained on the company\'s servers for a set period, even after you delete the chat. It may be reviewed by real human employees as part of a quality or safety sample.',
    },
    {
      q: 'What is the stranger test, in one sentence?',
      a: 'Before you paste anything into an AI, ask whether you would be comfortable if a stranger on a bus read it over your shoulder. If the answer is no, do not paste it.',
    },
  ];

  return (
    <Section
      id="sec-quick-check"
      label="Quick check"
      eyebrow="Five questions. Answer first. Then reveal."
    >
      <div className="space-y-3">
        {items.map((it, i) => (
          <QuickCheckItem key={i} n={i + 1} q={it.q} a={it.a} />
        ))}
      </div>
    </Section>
  );
}

function QuickCheckItem({ n, q, a }: { n: number; q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-line bg-surface">
      <div className="flex items-start gap-3 p-5">
        <span className="mt-0.5 font-mono text-[11px] uppercase tracking-widest text-amber">
          Q{n}
        </span>
        <div className="flex-1">
          <p className="text-text">{q}</p>
          {open && (
            <div className="mt-3 rounded-md border border-amber/30 bg-amber/[0.04] p-3 text-sm text-text">
              <p className="font-mono text-[10px] uppercase tracking-widest text-amber">
                Model answer
              </p>
              <p className="mt-1.5">{a}</p>
            </div>
          )}
        </div>
        <button
          onClick={() => setOpen((v) => !v)}
          className="rounded-md border border-line px-3 py-1.5 text-xs text-muted hover:border-amber hover:text-text"
        >
          {open ? 'Hide' : 'Reveal'}
        </button>
      </div>
    </div>
  );
}

// ---------- Lock card ----------

function LockCard() {
  return (
    <Section
      id="sec-lock-card"
      label="Lock card"
      eyebrow="Screenshot this. The shape of the whole landscape on one card."
    >
      <div className="rounded-xl border border-amber/40 bg-gradient-to-br from-amber/[0.05] to-amber/[0.02] p-8" data-lockcard-body>
        <div className="flex items-baseline justify-between">
          <p className="font-mono text-[11px] uppercase tracking-widest text-amber">
            Lesson 1.5 · Lock card
          </p>
          <button className="text-xs text-muted hover:text-text">
            ⤓ Save image
          </button>
        </div>

        <p className="mt-4 text-lg leading-relaxed text-text">
          AI is an umbrella. The thing you talk to is a chatbot or agent
          powered by an LLM. What you type goes to a server, may be used to
          train the next model, and may be kept and reviewed.
        </p>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="rounded-lg border border-line bg-ink/60 p-5">
            <p className="font-mono text-[10px] uppercase tracking-widest text-amber">
              The map
            </p>
            <ul className="mt-3 space-y-1.5 text-sm text-text/90">
              <li>
                <span className="font-mono text-amber">AI</span> · everything
                that mimics human intelligence
              </li>
              <li>
                <span className="font-mono text-amber">ML</span> · AI that
                learns from data
              </li>
              <li>
                <span className="font-mono text-amber">GenAI</span> · ML that
                creates new content
              </li>
              <li>
                <span className="font-mono text-amber">LLMs</span> · GenAI for
                language
              </li>
              <li>
                <span className="font-mono text-amber">Chatbots/Agents</span>{' '}
                · how you actually talk to LLMs
              </li>
            </ul>
          </div>
          <div className="rounded-lg border border-line bg-ink/60 p-5">
            <p className="font-mono text-[10px] uppercase tracking-widest text-amber">
              The three rules
            </p>
            <ul className="mt-3 space-y-1.5 text-sm text-text/90">
              <li>
                <span className="font-mono text-amber">1.</span> Stranger test
                · would you be okay if a stranger read this?
              </li>
              <li>
                <span className="font-mono text-amber">2.</span> Check the
                settings · once per tool, then move on
              </li>
              <li>
                <span className="font-mono text-amber">3.</span> Assume
                permanence · once it leaves your machine, it is out
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-6 rounded-lg border border-line bg-ink/60 p-5">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
            One sentence to remember
          </p>
          <p className="mt-2 text-sm leading-relaxed text-text/90">
            <span className="italic">Modality</span> is the type of input or
            output: text, image, audio, video. The same model can be reliable
            in one modality and unreliable in another. Audit accordingly.
          </p>
        </div>

        <p className="mt-6 text-sm italic text-muted">
          Week 1 is now complete. You know what AI is, how it works, what to
          call it, how to check it, and what happens to what you say to it.
          Week 2 builds on that floor.
        </p>
      </div>
    </Section>
  );
}

// ---------- Landscape map (nested boxes) ----------

function LandscapeMap() {
  return (
    <div className="my-6 rounded-xl border border-line bg-surface p-6">
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        The AI landscape · biggest to smallest
      </p>

      <div className="mt-5 space-y-3">
        <MapLayer
          level={0}
          label="AI · Artificial Intelligence"
          body="Any system that mimics tasks we associate with human intelligence. Spam filters, fraud detection, self-driving cars, chess engines. Most of it is not what you talk to."
        />
        <MapLayer
          level={1}
          label="ML · Machine Learning"
          body="AI that learns patterns from data instead of being explicitly programmed. Your email spam filter is ML. Netflix's recommendations are ML. Most modern AI is ML."
        />
        <MapLayer
          level={2}
          label="GenAI · Generative AI"
          body="ML that creates new content: text, images, audio, video. Anything that produces something that did not exist before, instead of just classifying or predicting."
        />
        <MapLayer
          level={3}
          label="LLMs · Large Language Models"
          body="GenAI specialized in language. GPT, Claude, Gemini, Llama. The engine behind every chatbot you have used in the last two years."
        />
        <MapLayer
          level={4}
          label="Chatbots and Agents"
          body="The product wrapped around an LLM. ChatGPT is a chatbot. A coding assistant that runs tools on your behalf is an agent. This is what you actually talk to."
          highlight
        />
      </div>

      <p className="mt-5 text-xs italic text-muted">
        Each box sits inside the box above it. When you use ChatGPT, you are
        using all five layers at once.
      </p>
    </div>
  );
}

function MapLayer({
  level,
  label,
  body,
  highlight = false,
}: {
  level: number;
  label: string;
  body: string;
  highlight?: boolean;
}) {
  const pad = ['pl-0', 'pl-4', 'pl-8', 'pl-12', 'pl-16'][level] || 'pl-0';
  return (
    <div className={pad}>
      <div
        className={`rounded-lg border px-4 py-3 ${
          highlight
            ? 'border-amber/50 bg-amber/[0.06]'
            : 'border-line bg-ink/40'
        }`}
      >
        <p
          className={`font-mono text-[11px] uppercase tracking-widest ${
            highlight ? 'text-amber' : 'text-muted'
          }`}
        >
          {label}
        </p>
        <p className="mt-1.5 text-sm leading-relaxed text-text/90">{body}</p>
      </div>
    </div>
  );
}

// ---------- Data flow (4 steps) ----------

function DataFlow() {
  const steps = [
    {
      n: 1,
      title: 'Travel',
      body: 'Your message leaves your device and travels over the internet to the AI company\'s servers. Encrypted in transit, in almost all modern products.',
    },
    {
      n: 2,
      title: 'Pre-process',
      body: 'On the server, your text gets tokenized (broken into tokens, from Lesson 1.2) and checked against the company\'s safety filters. Some prompts get blocked here before reaching the model.',
    },
    {
      n: 3,
      title: 'Generate',
      body: 'The pre-processed prompt goes into the LLM. The model produces a response one token at a time, based on patterns from its training. This is the only step where the actual AI work happens.',
    },
    {
      n: 4,
      title: 'Return and store',
      body: 'The response travels back to your screen. A copy of the full conversation typically gets logged on the server: prompt, response, timestamp, account ID. Whether and how long that copy is kept depends on the product\'s policy.',
    },
  ];

  return (
    <div className="my-6 rounded-xl border border-line bg-surface p-6">
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        What happens when you hit Send · 4 steps
      </p>
      <div className="mt-5 space-y-3">
        {steps.map((s) => (
          <div
            key={s.n}
            className="flex gap-4 rounded-lg border border-line bg-ink/40 p-4"
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-amber font-mono text-xs font-semibold text-ink">
              {s.n}
            </span>
            <div className="min-w-0">
              <p className="font-mono text-[11px] uppercase tracking-widest text-amber">
                {s.title}
              </p>
              <p className="mt-1.5 text-sm leading-relaxed text-text/90">
                {s.body}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 rounded-lg border border-line/70 border-dashed bg-ink/20 p-4">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          One exception worth knowing · local LLMs
        </p>
        <p className="mt-1.5 text-sm leading-relaxed text-muted">
          A small but growing category of AI runs the model directly on your
          device (your laptop, phone, or a machine you control). Tools like
          Ollama, LM Studio, and Apple Intelligence on newer iPhones fall in
          this bucket. When the model runs locally, your prompt never leaves
          your machine and Steps 1, 2, and 4 above mostly do not apply. Local
          models are usually smaller and less capable than the frontier chat
          products, but the privacy math is very different. For everything
          else in this program, assume the 4-step flow above.
        </p>
      </div>
    </div>
  );
}

// ---------- Data fate card ----------

function DataFateCard({
  n,
  title,
  body,
}: {
  n: number;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-lg border border-line bg-surface p-5">
      <div className="flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-md bg-amber font-mono text-xs font-semibold text-ink">
          {n}
        </span>
        <p className="font-mono text-[11px] uppercase tracking-widest text-amber">
          {title}
        </p>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-text/90">{body}</p>
    </div>
  );
}

// ---------- Rule card ----------

function RuleCard({
  n,
  title,
  line,
}: {
  n: number;
  title: string;
  line: string;
}) {
  return (
    <div className="rounded-lg border border-line bg-surface p-5">
      <div className="flex items-center gap-3">
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-amber font-mono text-sm font-semibold text-ink">
          {n}
        </span>
        <p className="text-base font-semibold text-text">{title}</p>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-text/90">{line}</p>
    </div>
  );
}

// ---------- Subhead ----------

function Subhead({ n, title }: { n: string; title: string }) {
  return (
    <h3 className="mt-8 flex items-baseline gap-3 text-lg font-semibold text-text">
      <span className="font-mono text-[11px] uppercase tracking-widest text-amber">
        {n}
      </span>
      {title}
    </h3>
  );
}

// ---------- Vocab card (single-term, mirrors 1.4 pattern) ----------

function VocabCard({
  term,
  definition,
  analogy,
  hook,
  matters,
}: {
  term: string;
  definition: string;
  analogy: string;
  hook: string;
  matters: string;
}) {
  const [draft, setDraft] = useState('');
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="my-6 overflow-hidden rounded-xl border border-line bg-surface">
      <div className="border-b border-line bg-elevated/40 px-5 py-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          Vocab · 1.5 · New term
        </p>
        <h4 className="mt-1 text-xl font-semibold text-text">{term}</h4>
      </div>
      <div className="space-y-4 px-5 py-5">
        {!revealed && (
          <>
            <p className="text-sm text-muted">
              Write your own one-sentence definition first. Then reveal.
            </p>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={`Your definition of "${term}"...`}
              className="h-20 w-full resize-none rounded-md border border-line bg-ink/60 px-3 py-2 text-sm text-text placeholder:text-muted/60 focus:border-amber focus:outline-none"
            />
            <button
              onClick={() => setRevealed(true)}
              className="rounded-md bg-amber px-4 py-2 text-xs font-semibold text-ink hover:bg-amber-hover"
            >
              Reveal definition →
            </button>
          </>
        )}
        {revealed && (
          <div className="space-y-3">
            <Block label="Definition" body={definition} />
            <Block label="Analogy" body={analogy} accent="amber" />
            <Block label="Memory hook" body={hook} />
            <Block label="Why it matters" body={matters} />
          </div>
        )}
      </div>
    </div>
  );
}

function Block({
  label,
  body,
  accent = 'plain',
}: {
  label: string;
  body: string;
  accent?: 'plain' | 'amber';
}) {
  return (
    <div
      className={`rounded-md border px-4 py-3 ${
        accent === 'amber'
          ? 'border-amber/40 bg-amber/[0.04]'
          : 'border-line bg-ink/40'
      }`}
    >
      <p
        className={`font-mono text-[10px] uppercase tracking-widest ${
          accent === 'amber' ? 'text-amber' : 'text-muted'
        }`}
      >
        {label}
      </p>
      <p className="mt-1.5 text-sm leading-relaxed text-text/90">{body}</p>
    </div>
  );
}

// ---------- Next up ----------

function NextUp() {
  return (
    <div className="mt-12 rounded-lg border border-line bg-surface p-6" data-nextup-card>
      <p className="font-mono text-[11px] uppercase tracking-widest text-muted">
        Week 1 complete · Up next · Week 2
      </p>
      <div className="mt-2 flex items-start justify-between gap-6">
        <div>
          <h3 className="text-xl font-semibold text-text">
            Not all models are the same
          </h3>
          <p className="mt-2 text-sm text-muted">
            Week 2 opens with the tooling pre-check. Before you run the four
            audit checks, you should know what model you are even talking to.
            Frontier vs cheap, web search on or off, reasoning mode or not.
            Same prompt, very different answers.
          </p>
        </div>
        <button className="shrink-0 rounded-md border border-line px-4 py-2 text-sm text-muted hover:border-amber hover:text-text">
          Coming soon
        </button>
      </div>
    </div>
  );
}

// ---------- Shared primitives ----------

function Vocab({ children }: { children: React.ReactNode }) {
  return (
    <span className="border-b border-dotted border-amber/60 text-text">
      {children}
    </span>
  );
}

function Section({
  id,
  label,
  eyebrow,
  accent = 'plain',
  children,
}: {
  id: string;
  label: string;
  eyebrow: string;
  accent?: 'amber' | 'blue' | 'rose' | 'plain';
  children: React.ReactNode;
}) {
  const colorMap = {
    amber: 'text-amber',
    blue: 'text-[#7DC8E8]',
    rose: 'text-[#E89F8B]',
    plain: 'text-text',
  };
  return (
    <section
      id={id}
      className="scroll-mt-24 border-t border-line py-12 first-of-type:border-t-0 first-of-type:pt-0"
    >
      <div className="mb-6">
        <p
          className={`font-mono text-[11px] uppercase tracking-[0.18em] ${colorMap[accent]}`}
        >
          {label}
        </p>
        <p className="mt-1.5 text-sm text-muted">{eyebrow}</p>
      </div>
      <div className="space-y-4 text-base leading-relaxed text-text/90">
        {children}
      </div>
    </section>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    { id: 'sec-cold-open', label: 'Cold open' },
    { id: 'sec-from-last-time', label: 'From last time' },
    { id: 'sec-teaching', label: 'The map' },
    { id: 'sec-your-data', label: 'Your data' },
    { id: 'sec-quick-check', label: 'Quick check' },
    { id: 'sec-lock-card', label: 'Lock card' },
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ul className="mt-4 space-y-1">
        {items.map((it, i) => {
          const active = i === activeIndex;
          const done = i < activeIndex;
          return (
            <li key={it.id}>
              <a
                href={`#${it.id}`}
                className={`flex items-center gap-3 rounded-md px-2.5 py-2 text-sm transition-colors ${
                  active
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:bg-elevated/60 hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-full border font-mono text-[10px] ${
                    active
                      ? 'border-amber bg-amber text-ink'
                      : done
                      ? 'border-amber/40 text-amber'
                      : 'border-line text-muted'
                  }`}
                >
                  {done ? '✓' : i + 1}
                </span>
                {it.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}
